import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import {
  DeleteMessage,
  LoadMessage,
  LoadMessageTemplates,
  MessagesState,
  UpdateMessage,
} from '@vandelft/modules/shared/state/messages';
import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { Message, MessageTemplate } from '@vandelft/modules/shared/models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment-timezone';
import { Navigate } from '@ngxs/router-plugin';
import { EnvironmentState } from '@vandelft/modules/shared/state/environment';
import { NOTIFICATION_CHANNEL_OPTIONS, REVIEW_REQUEST_EMAIL_TEMPLATE } from '@vandelft/modules/shared/constants';
import { NotificationChannel } from '@vandelft/shared/interfaces';
import { NotificationHelper } from '@vandelft/shared/helpers';

@Component({
    selector: 'app-edit-message',
    styleUrls: ['./edit-message.component.scss'],
    templateUrl: './edit-message.component.html',
    standalone: false
})
export class EditMessageComponent implements OnInit, OnDestroy {
  @Select(MessagesState.message) public message$: Observable<Message>;
  @Select(MessagesState.templates) public templates$: Observable<MessageTemplate[]>;
  public prefix$: Observable<string> = inject(Store).select(EnvironmentState.prefix);

  public message: Message;
  public previewTemplate: MessageTemplate;
  public templates: MessageTemplate[];

  public channelOptions = NOTIFICATION_CHANNEL_OPTIONS;

  /** Both channels send the body as plain text instead of a template when `template` is empty. */
  public useCustomText = false;
  public isBusy = false;

  private subscriptions: Subscription[] = [];

  public constructor(
    private store: Store,
    private router: ActivatedRoute,
    private cdr: ChangeDetectorRef,
  ) {}

  public form: FormGroup = new FormGroup({
    id: new FormControl(null, [Validators.required]),
    channel: new FormControl(null),
    message: new FormControl(null),
    template: new FormControl(null),
    subject: new FormControl(null),
    scheduled: new FormControl(null),
  });

  public ngOnInit(): void {
    this.store.dispatch(new LoadMessageTemplates());
    this.subscriptions.push(
      this.router.params.subscribe((params: Params): void => {
        this.store.dispatch(new LoadMessage(params.id));
      }),
      this.message$.subscribe((message: Message): void => {
        if (message === null) {
          return;
        }

        this.message = message;
        this.previewTemplate = this.getTemplate(this.message.template, this.templates);
        this.useCustomText = !message.template;
        // Silently, so loading a message does not trip the channel handler below and wipe its template.
        this.form.patchValue(
          {
            ...message,
            // `datetime-local` only accepts `YYYY-MM-DDTHH:mm`; anything else and the browser
            // silently blanks the field, hiding the schedule that is actually set.
            scheduled: !message.scheduled
              ? null
              : moment.tz(message.scheduled, moment.tz.guess()).format('YYYY-MM-DDTHH:mm'),
          },
          { emitEvent: false },
        );

        this.cdr.detectChanges();
      }),
      this.templates$.subscribe((templates) => {
        this.templates = templates;
        this.previewTemplate = this.getTemplate(this.message?.template, this.templates);
        this.cdr.detectChanges();
      }),
      this.form.get('channel').valueChanges.subscribe((channel: NotificationChannel): void => {
        // The backend re-derives the recipient on a flip but leaves `template` alone, and the two
        // channels draw from entirely different template sets — an email view versus a Meta template.
        this.useCustomText = false;
        this.form.get('template').setValue(channel === 'email' ? REVIEW_REQUEST_EMAIL_TEMPLATE : null);
        this.cdr.detectChanges();
      }),
      this.form.get('template').valueChanges.subscribe(async (template) => {
        const templates = await firstValueFrom(this.templates$);
        this.previewTemplate = this.getTemplate(template, templates);
        this.cdr.detectChanges();
      })
    );

  }

  public getTemplate(template: string, templates: MessageTemplate[]): MessageTemplate {
    return templates?.find((t: MessageTemplate): boolean => t.name === template);
  }

  public get channel(): NotificationChannel {
    return this.form.get('channel').value;
  }

  public isReachable(channel: NotificationChannel): boolean {
    return NotificationHelper.isReachable(channel, this.message?.address);
  }

  public getChannelOptionLabel(option: { value: string; label: string }): string {
    if (this.isReachable(option.value as NotificationChannel)) {
      return option.label;
    }

    return option.value === 'email'
      ? `${option.label} (geen e-mailadres bekend)`
      : `${option.label} (geen telefoonnummer bekend)`;
  }

  public toggleCustomText(useCustomText: boolean): void {
    this.useCustomText = useCustomText;
    this.form.get('template').setValue(useCustomText ? null : REVIEW_REQUEST_EMAIL_TEMPLATE);
  }

  /** Without a template or a body there is nothing to deliver, whichever channel is selected. */
  public canSubmit(): boolean {
    if (this.form.invalid || this.isBusy) {
      return false;
    }

    return !!this.form.get('template').value || !!this.form.get('message').value?.trim();
  }

  public async submit(scheduleNow: boolean = false): Promise<void> {
    const message = { ...this.form.value };
    if (!message.id) {
      return;
    }

    if (!message.channel) {
      delete message.channel;
    }

    if ((message.scheduled as any) === '') {
      message.scheduled = null;
    }
    if (scheduleNow) {
      message.scheduled = moment.utc().format('YYYY-MM-DD HH:mm:ss');
    } else if (message.scheduled !== null) {
      message.scheduled = moment.tz(message.scheduled, moment.tz.guess()).utc().format('YYYY-MM-DD HH:mm:ss');
    }

    try {
      this.isBusy = true;
      await firstValueFrom(this.store.dispatch(new UpdateMessage(message)));
    } catch (e) {
      // The interceptor already showed the server's message; stay put so it can be acted on.
      return;
    } finally {
      this.isBusy = false;
    }

    const prefix: string = this.store.selectSnapshot(EnvironmentState.prefix);
    this.store.dispatch(new Navigate([`/${prefix}/messages`]));
  }

  public delete(id: string): void {
    if (!confirm('Weet je zeker dat je dit bericht wilt verwijderen?')) {
      return;
    }
    this.store.dispatch(new DeleteMessage(id));
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }
}
